import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { TimeRecord, Project } from '@/types';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { formatMinutes } from './reports';

/**
 * PDFレポートを生成
 */
export async function generatePDFReport(
  records: TimeRecord[],
  _projects: Project[],
  startDate: Date,
  endDate: Date,
  title: string = '稼働実績レポート'
): Promise<void> {
  const doc = new jsPDF();

  // ヘッダー
  doc.setFontSize(20);
  doc.text(title, 14, 20);

  // 期間
  doc.setFontSize(12);
  const periodText = `${format(startDate, 'yyyy/MM/dd', { locale: ja })} - ${format(
    endDate,
    'yyyy/MM/dd',
    { locale: ja }
  )}`;
  doc.text(periodText, 14, 30);

  // サマリー統計
  const totalMinutes = records.reduce((sum, r) => sum + (r.totalMinutes || 0), 0);
  const totalHours = Math.round(totalMinutes / 60 * 10) / 10;
  const workDays = new Set(records.map((r) => format(r.clockInTime.toDate(), 'yyyy-MM-dd'))).size;

  doc.setFontSize(10);
  doc.text(`Total Records: ${records.length}`, 14, 40);
  doc.text(`Total Hours: ${totalHours}h (${formatMinutes(totalMinutes)})`, 14, 46);
  doc.text(`Work Days: ${workDays}`, 14, 52);

  // テーブルデータの準備
  const tableData = records.map((record) => [
    format(record.clockInTime.toDate(), 'yyyy/MM/dd (E)', { locale: ja }),
    record.project?.name || '-',
    record.project?.code || '-',
    format(record.clockInTime.toDate(), 'HH:mm'),
    record.clockOutTime ? format(record.clockOutTime.toDate(), 'HH:mm') : '-',
    `${Math.round((record.totalMinutes || 0) / 60 * 10) / 10}h`,
    formatMinutes(record.totalMinutes || 0),
    record.memo || record.notes || '-',
  ]);

  // テーブルを生成
  autoTable(doc, {
    startY: 60,
    head: [['Date', 'Project', 'Code', 'Start', 'End', 'Hours', 'Time', 'Memo']],
    body: tableData,
    styles: {
      font: 'NotoSansJP',
      fontSize: 8,
      cellPadding: 2,
    },
    headStyles: {
      fillColor: [59, 130, 246],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [245, 247, 250],
    },
    columnStyles: {
      0: { cellWidth: 28 },
      1: { cellWidth: 30 },
      2: { cellWidth: 20 },
      3: { cellWidth: 18 },
      4: { cellWidth: 18 },
      5: { cellWidth: 18 },
      6: { cellWidth: 18 },
      7: { cellWidth: 'auto' },
    },
    margin: { left: 14, right: 14 },
  });

  // プロジェクト別サマリー
  const projectStats: { [key: string]: { name: string; minutes: number; count: number } } = {};
  records.forEach((record) => {
    const projectId = record.projectId;
    const pid = projectId || 'unknown';
if (!projectStats[pid]) {
  projectStats[pid] = {
    name: record.project?.name || '不明',
    minutes: 0,
    count: 0,
  };
}
projectStats[pid].minutes += record.totalMinutes || 0;
projectStats[pid].count += 1;
  });

  const projectSummaryData = Object.values(projectStats).map((stat) => [
    stat.name,
    stat.count,
    `${Math.round(stat.minutes / 60 * 10) / 10}h`,
    formatMinutes(stat.minutes),
    `${Math.round((stat.minutes / totalMinutes) * 100)}%`,
  ]);

  // プロジェクト別サマリーテーブル
  const finalY = (doc as any).lastAutoTable.finalY + 10;
  doc.setFontSize(14);
  doc.text('Project Summary', 14, finalY);

  autoTable(doc, {
    startY: finalY + 6,
    head: [['Project', 'Count', 'Hours', 'Time', '%']],
    body: projectSummaryData,
    styles: {
      font: 'NotoSansJP',
      fontSize: 9,
      cellPadding: 3,
    },
    headStyles: {
      fillColor: [16, 185, 129],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [245, 247, 250],
    },
    margin: { left: 14, right: 14 },
  });

  // フッター
  const pageCount = doc.internal.pages.length - 1;
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(
      `Generated: ${format(new Date(), 'yyyy/MM/dd HH:mm', { locale: ja })}`,
      14,
      doc.internal.pageSize.height - 10
    );
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width - 30,
      doc.internal.pageSize.height - 10
    );
  }

  // PDFをダウンロード
  const filename = `timerecords_${format(startDate, 'yyyyMMdd')}_${format(
    endDate,
    'yyyyMMdd'
  )}.pdf`;
  doc.save(filename);
}

/**
 * シンプルなPDFレポートを生成（フォールバック版）
 */
export function generateSimplePDFReport(
  records: TimeRecord[],
  startDate: Date,
  endDate: Date
): void {
  const doc = new jsPDF();

  // タイトル
  doc.setFontSize(18);
  doc.text('Time Records Report', 14, 20);

  // 期間
  doc.setFontSize(12);
  doc.text(
    `Period: ${format(startDate, 'yyyy/MM/dd')} - ${format(endDate, 'yyyy/MM/dd')}`,
    14,
    30
  );

  // 統計
  const totalMinutes = records.reduce((sum, r) => sum + (r.totalMinutes || 0), 0);
  const totalHours = Math.round(totalMinutes / 60 * 10) / 10;

  doc.setFontSize(10);
  doc.text(`Total Records: ${records.length}`, 14, 40);
  doc.text(`Total Hours: ${totalHours}h`, 14, 46);

  // テーブル
  const tableData = records.map((record) => [
    format(record.clockInTime.toDate(), 'yyyy/MM/dd'),
    record.project?.name || '-',
    format(record.clockInTime.toDate(), 'HH:mm'),
    record.clockOutTime ? format(record.clockOutTime.toDate(), 'HH:mm') : '-',
    `${Math.round((record.totalMinutes || 0) / 60 * 10) / 10}h`,
  ]);

  autoTable(doc, {
    startY: 55,
    head: [['Date', 'Project', 'Start', 'End', 'Hours']],
    body: tableData,
    styles: { fontSize: 9 },
    headStyles: { fillColor: [59, 130, 246] },
  });

  // ダウンロード
  const filename = `report_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`;
  doc.save(filename);
}
